import { useState } from 'react';
import { Card, CardHeader } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { useTransactions, useCashbookCategories, useCreateTransaction } from './hooks/useTransactions';
import { format } from 'date-fns';
import { PlusCircle, ArrowUpRight, ArrowDownRight, IndianRupee } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import type { WidgetConfig } from '../../infrastructure/contracts/widget.contract';

export const expenseLedgerConfig: WidgetConfig = {
  id: 'accounts-expense-ledger',
  title: 'Expense Ledger',
  description: 'Cash & Bank transactions via double-entry ledger',
  category: 'list',
  refreshStrategy: 'manual',
  defaultSize: { w: 12, h: 5 },
  priority: 10,
};

export function ExpenseLedgerWidget() {
  const { data: cashbook, isLoading } = useTransactions();
  const { data: categories } = useCashbookCategories();
  const createMutation = useCreateTransaction();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [type, setType] = useState<'in' | 'out'>('out');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [cashAccount, setCashAccount] = useState('Cash');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [narration, setNarration] = useState('');

  const entries = cashbook?.entries || [];
  const currentBalance = cashbook?.balance || 0;

  const categoryOptions = type === 'out'
    ? (categories?.expenseCategories || []).map(c => ({ value: c, label: c }))
    : (categories?.incomeCategories || []).map(c => ({ value: c, label: c }));

  const cashAccountOptions = (categories?.cashAccounts || ['Cash', 'Bank', 'Petty Cash']).map(c => ({ value: c, label: c }));

  const handleCreate = async () => {
    if (!amount || !category || !date) return;
    await createMutation.mutateAsync({
      type,
      amount: parseFloat(amount),
      category,
      cashAccount,
      date,
      narration,
    });
    setIsModalOpen(false);
    setAmount('');
    setCategory('');
    setNarration('');
  };

  const addTransactionFooter = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
      <Button disabled={createMutation.isPending} onClick={handleCreate} variant="primary">
        {createMutation.isPending ? 'Posting...' : 'Post Entry'}
      </Button>
    </div>
  );

  return (
    <Card className="col-span-full shadow-sm rounded-xl border border-outline">
      <CardHeader
        title={<div className="text-xl font-bold text-on-surface flex items-center gap-2">Expense Ledger</div>}
        subtitle={
          <div className="text-on-surface-variant">
            Cash & Bank Transactions (Double-Entry). Balance: <span className={currentBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>₹{currentBalance.toLocaleString()}</span>
          </div>
        }
        action={
          <Button size="sm" onClick={() => setIsModalOpen(true)} variant="primary">
            <PlusCircle className="h-4 w-4 mr-2" /> Add Entry
          </Button>
        }
        className="pb-4 border-b border-outline"
      />

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} title="Add Cashbook Entry" footer={addTransactionFooter} size="sm">
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Type</label>
            <Select name="type" value={type} onChange={e => { setType(e.target.value as any); setCategory(''); }}
              options={[{ value: 'out', label: 'Money Out (Expense)' }, { value: 'in', label: 'Money In (Income)' }]} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Amount</label>
            <Input name="amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Category</label>
            <Select name="category" value={category} onChange={e => setCategory(e.target.value)}
              options={[{ value: '', label: '— Select category —' }, ...categoryOptions]} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Paid From / To</label>
            <Select name="cashAccount" value={cashAccount} onChange={e => setCashAccount(e.target.value)} options={cashAccountOptions} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Date</label>
            <Input name="date" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-label-md text-on-surface">Narration / Notes</label>
            <textarea value={narration} onChange={e => setNarration(e.target.value)}
              className="w-full rounded-md border border-outline bg-surface-variant px-3 py-2 text-body-md text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none" rows={2} placeholder="Optional description" />
          </div>
        </div>
      </Modal>

      <div className="w-full p-0">
        <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin"></div>
              Loading ledger...
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-on-surface-variant/70 bg-surface-variant/50 m-4 rounded-lg border border-outline/50">
              <IndianRupee className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No transactions yet. Add your first entry above.
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-surface-variant text-on-surface-variant sticky top-0 z-10 shadow-sm border-b border-outline">
                <tr>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3 font-medium">Narration</th>
                  <th className="px-6 py-3 font-medium">Account</th>
                  <th className="px-6 py-3 font-medium">In</th>
                  <th className="px-6 py-3 font-medium">Out</th>
                  <th className="px-6 py-3 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline/50">
                {entries.map((t) => (
                  <tr key={t.id} className="hover:bg-surface-variant/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-on-surface">
                      {format(new Date(t.date), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-4 text-on-surface">
                      <div className="font-medium text-on-surface truncate max-w-[250px]">{t.narration || t.voucherType}</div>
                    </td>
                    <td className="px-6 py-4 text-on-surface-variant text-xs">
                      {t.accountName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-emerald-600 dark:text-emerald-400 font-medium">
                      {t.debit > 0 ? (
                        <span className="flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> ₹{t.debit.toLocaleString()}</span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-rose-600 dark:text-rose-400 font-medium">
                      {t.credit > 0 ? (
                        <span className="flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> ₹{t.credit.toLocaleString()}</span>
                      ) : '-'}
                    </td>
                    <td className={cn("px-6 py-4 whitespace-nowrap text-right font-semibold", t.runningBalance >= 0 ? "text-emerald-600 dark:text-emerald-500" : "text-rose-600 dark:text-rose-500")}>
                      ₹{t.runningBalance.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Card>
  );
}
